
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    getLeaveApplicationById,
    updateLeaveApplication,
    getAllEmployees,
    getAllLeaveTypesForApplication,
    getAllSupervisors,
    getLeaveStats
} from 'adminApi/LeaveApi/LeaveApplicationApi';
import { validateForm } from "utils/validation";
import { LeaveApplicationRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import EditLeaveApplicationForm from "./Components/EditLeaveApplicationForm";
import fields from "../json/LeaveApplicationFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchFormDataById from "Hooks/useFetchFormDataById";

const EditLeaveApplication = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { formdata } = fields;
    const [options, setOptions] = useState({ employee: [], leave_type: [], supervisor: [] });
    const [leaveStats, setLeaveStats] = useState({ enjoyed_days: 0, total_days: 0 });

    // Initialize form values
    const initialValues = formdata.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {});

    const { values, errors, handleChange, setValues, handleFileChange, setErrors } = useFormChange(
        initialValues,
        LeaveApplicationRules
    );

    // Fetch initial options
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const applicationYear = values.applied_start_date ?
                    new Date(values.applied_start_date).getFullYear() :
                    new Date().getFullYear();

                const [empRes, typeRes, supRes] = await Promise.all([
                    getAllEmployees(),
                    getAllLeaveTypesForApplication(applicationYear),
                    getAllSupervisors()
                ]);

                setOptions({
                    employee: empRes.success ? empRes.data.map(e => ({ id: e.employee_id, name: e.employee_name })) : [],
                    leave_type: typeRes.success ? typeRes.data.map(t => ({ id: t.leave_type_id, name: t.leave_type })) : [],
                    supervisor: supRes.success ? supRes.data.map(s => ({ id: s.employee_id, name: s.supervisor_name })) : []
                });
            } catch (error) {
                console.error("Error fetching options:", error);
            }
        };
        fetchOptions();
    }, [values.applied_start_date]);

    // Fetch existing data
    const { loading: loadingFetch } = useFetchFormDataById(
        id,
        formdata,
        getLeaveApplicationById,
        setValues
    );

    // Auto-calculate applied_days
    useEffect(() => {
        if (values.applied_start_date && values.applied_end_date) {
            const start = new Date(values.applied_start_date);
            const end = new Date(values.applied_end_date);
            if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                start.setHours(0, 0, 0, 0);
                end.setHours(0, 0, 0, 0);
                const diffTime = Math.abs(end - start);
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
                const multiplier = Number(values.day_type) === 0 ? 0.5 : 1;
                setValues(prev => ({ ...prev, applied_days: Math.max(diffDays * multiplier, 0) }));
            }
        }
    }, [values.applied_start_date, values.applied_end_date, values.day_type, setValues]);

    // Auto-calculate approved_days
    useEffect(() => {
        if (values.approved_start_date && values.approved_end_date) {
            const start = new Date(values.approved_start_date);
            const end = new Date(values.approved_end_date);
            if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                start.setHours(0, 0, 0, 0);
                end.setHours(0, 0, 0, 0);
                const diffTime = Math.abs(end - start);
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
                const multiplier = Number(values.day_type) === 0 ? 0.5 : 1;
                setValues(prev => ({ ...prev, approved_days: Math.max(diffDays * multiplier, 0) }));
            }
        }
    }, [values.approved_start_date, values.approved_end_date, values.day_type, setValues]);

    // Fetch leave stats when employee_id or leave_type_id changes
    useEffect(() => {
        if (values.employee_id && values.leave_type_id) {
            const fetchStats = async () => {
                const res = await getLeaveStats({ employee_id: values.employee_id, leave_type_id: values.leave_type_id });
                if (res.success) {
                    setLeaveStats(res.data);
                }
            };
            fetchStats();
        } else {
            setLeaveStats({ enjoyed_days: 0, total_days: 0 });
        }
    }, [values.employee_id, values.leave_type_id]);

    // Form submit handler
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();
            Object.entries(formValues).forEach(([key, val]) => {
                formData.append(key, val ?? "");
            });
            return await updateLeaveApplication({ id, data: formData });
        },
        (vals) => {
            if (typeof (vals.attachment) === "string") {
                delete vals.attachment
            }
            delete vals.attachmentPreview
            const err = validateForm(vals, LeaveApplicationRules)
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Leave Application updated successfully"
    );

    return (
        <>
            {(loadingFetch || loadingSubmit) && <Spinner isSpinner={true} />}
            <EditLeaveApplicationForm
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleFileChange={handleFileChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                imagePreview={values.attachmentPreview || values.attachment}
                loading={loadingSubmit}
                options={options}
                leaveStats={leaveStats}
            />
        </>
    );
};

export default EditLeaveApplication;
